import React, { Component } from 'react';
import PropTypes from 'prop-types';
import CardCarousel from '@homeaway/react-card-carousel';
import { AspectRatioCard } from '@homeaway/react-aspect-ratio-card';
import IsBrowser from '../IsBrowser';
import VisibilitySensor from 'react-visibility-sensor';

// IMPORTANT - This logic must be kept in sync with src/universal/components/Carousel/Carousel.less

import './Carousel.less';

const INITIAL_INDEX = -1;

class Carousel extends Component {
  static propTypes = {
    title: PropTypes.string,
    subheading: PropTypes.string,
    onPreviousClick: PropTypes.func,
    onNextClick: PropTypes.func,
    isTouchDevice: PropTypes.bool,
    cardAspectRatio: PropTypes.string,
    breakpoints: PropTypes.shape(),
    ssrEnabled: PropTypes.bool
  }

  static defaultProps = {
    title: '',
    subheading: '',
    cardAspectRatio: '4x3',
    breakpoints: {
      '1600': 5,
      '1200': 4,
      '992': 4,
      '768': 3,
      '480': 3,
      '0': 2
    },
    onPreviousClick: () => {},
    onNextClick: () => {},
    ssrEnabled: false
  }

  state = {
    index: 0,
    lastViewed: INITIAL_INDEX
  };

  initialLastViewed = INITIAL_INDEX;

  handleOnVisibilityChange = (isVisible) => {
    if (isVisible) {
      this.setState({
        lastViewed: this.initialLastViewed
      });
    }
  }

  handlePrevNext = cb => (evt, newIndex) => {
    this.setState({
      index: newIndex
    });
    cb();
  }

  handleOnCardsShownChange = (newIndex, allVisibleIndexes) => {
    const currentLastViewed = allVisibleIndexes[allVisibleIndexes.length - 1];

    // Store initial images until the module is viewed
    if (this.state.lastViewed === INITIAL_INDEX) {
      this.initialLastViewed = currentLastViewed;
      return;
    }


    if (this.state.lastViewed < currentLastViewed) {
      this.setState({
        lastViewed: currentLastViewed
      });
    }
  }

  getControlOffset = (aspectRatio) => {
    return (width, controlHeight) => {
      return AspectRatioCard.getControlOffset(width, controlHeight, aspectRatio);
    };
  }

  shouldDisplayCard(index) {
    return index <= this.state.lastViewed;
  }

  handleTouchDeviceScroll = () => {
    // When the user interacts with the mobile carousel, we load all the images at once
    this.setState({
      lastViewed: React.Children.count(this.props.children)
    });
  }

  areAllImagesDisplayed = () => {
    return this.state.lastViewed === React.Children.count(this.props.children) - 1;
  }

  render() {
    const {
      children,
      title,
      subheading,
      isTouchDevice,
      onPreviousClick,
      onNextClick,
      cardAspectRatio,
      breakpoints,
      ssrEnabled,
      ...otherProps
    } = this.props;

    const extendedChildren = React.Children.map(children, (child, index) => React.cloneElement(child, {
      loadImage: this.shouldDisplayCard(index)
    }));

    let content = (
      <VisibilitySensor
        partialVisibility
        active={this.state.lastViewed === INITIAL_INDEX}
        minTopValue={50}
        delayedCall
        onChange={this.handleOnVisibilityChange}
      >
        <CardCarousel
          breakpoints={breakpoints}
          cardIndex={this.state.index}
          onPreviousClick={this.handlePrevNext(onPreviousClick)}
          onNextClick={this.handlePrevNext(onNextClick)}
          title={title}
          subheading={subheading}
          scrolling={isTouchDevice}
          paging
          getControlOffset={this.getControlOffset(cardAspectRatio)}
          className="carousel"
          {...!this.areAllImagesDisplayed() && { onCardsShownChange: this.handleOnCardsShownChange }}
          {...otherProps}
        >
          {extendedChildren}
        </CardCarousel>
      </VisibilitySensor>
    );

    return ssrEnabled
      ? (
        <div
          className="carousel-ssr"
          {...isTouchDevice && !this.areAllImagesDisplayed() && { onScroll: this.handleTouchDeviceScroll }}
        >
          {content}
        </div>
      ) : (
        <IsBrowser
          withTransition
          placeHolder={<div className="carousel-placeholder" />}
          {...isTouchDevice && !this.areAllImagesDisplayed() && { onScroll: this.handleTouchDeviceScroll }}
        >
          {content}
        </IsBrowser>
      );
  }
}

export default Carousel;
